/**
 * WhatComesNextQuestionScreen - PreK What Comes Next Assessment
 *
 * Refactored to use shared hooks and components while preserving
 * all original business logic exactly.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { PreKSettings, DiagnosticAssessmentResult } from './types';
import { usePreKTTS, QUESTION_SCREEN_AUDIO } from '../../hooks/usePreKAudio';
import { useAuth } from '../../context/AuthContext';
import { progressQueue } from '../../services/ProgressQueueService';
import { recordPageTransition, completePreKAssessment } from '../../config/api';
import { FactStatus } from '../../types/progress';

// Shared components and constants
import { AnswerChoicesGrid, AssessmentHeader } from './shared';
import WarmupTransitionScreen from './WarmupTransitionScreen';
import ListenButton from '../common/ListenButton';
import {
  PREK_TRACKS,
  TIMER_CONSTANTS,
  FLUENCY_TARGETS_SEC,
  WARMUP_CONFIG,
  valueToFactId,
  NUMERAL_FONT_STYLE,
  TOTAL_ASSESSMENT_QUESTIONS,
  generateAssessmentSequence,
} from './preKConstants';

interface WhatComesNextQuestionScreenProps {
  settings: PreKSettings;
  onComplete: (results: DiagnosticAssessmentResult) => void;
}

interface NumberSequence {
  id: string;
  targetNumber: number;
  sequence: number[];
}

const TRACK_ID = PREK_TRACKS.WHATCOMESNEXT;

const WhatComesNextQuestionScreen: React.FC<WhatComesNextQuestionScreenProps> = ({
  settings,
  onComplete,
}) => {
  // ============================================================================
  // State Management
  // ============================================================================

  // Question tracking
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);

  // Warmup state
  const [isWarmup, setIsWarmup] = useState(true);
  const [warmupCount, setWarmupCount] = useState(0);
  const [showTransitionScreen, setShowTransitionScreen] = useState(false);

  // What comes next specific state
  const [currentSequence, setCurrentSequence] = useState<NumberSequence | null>(null);
  const [questionPhase, setQuestionPhase] = useState<'ready' | 'question'>('ready');
  const [showSequence, setShowSequence] = useState(false);

  // Answer state
  const [waitingForCorrectAnswer, setWaitingForCorrectAnswer] = useState(false);
  const [hasAnswered, setHasAnswered] = useState(false);
  const [wrongAnswer, setWrongAnswer] = useState<number | null>(null);
  const [answerChoices, setAnswerChoices] = useState<number[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  // Timer state
  const [activeTimeElapsed, setActiveTimeElapsed] = useState(0);
  const [questionTimeRemaining, setQuestionTimeRemaining] = useState<number>(
    TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]
  );
  const [questionTimerActive, setQuestionTimerActive] = useState(false);
  const [timerActive, setTimerActive] = useState(false);

  // TTS state
  const [ttsHasPlayed, setTtsHasPlayed] = useState(false);

  // ============================================================================
  // Refs
  // ============================================================================

  const { user, updateUserField } = useAuth();
  const testStartTime = useRef<number>(Date.now());
  const questionStartTime = useRef<number>(Date.now());
  const timerActiveStartTime = useRef<number>(0);
  const accumulatedTimerTime = useRef<number>(0);
  const questionActiveStartRef = useRef<number | null>(null);
  const questionAccumulatedTimeRef = useRef<number>(0);
  const hasCompletedRef = useRef<boolean>(false);
  const isFirstQuestionRef = useRef(true);
  const questionSequenceRef = useRef<number[]>([]);

  // TTS integration
  const { playTTSWithCallback, isTTSPlaying, playChime } = usePreKTTS();

  // Store generateNextSequence in a ref to avoid dependency issues
  const generateNextSequenceRef = useRef<() => NumberSequence>(() => ({
    id: 'placeholder',
    targetNumber: 3,
    sequence: [1, 2],
  }));

  // ============================================================================
  // Helper Functions
  // ============================================================================

  // Generate number sequence patterns
  const generateNumberSequence = useCallback((targetNumber: number): NumberSequence => {
    // Create sequence showing "1, 2, ?" where target is 3
    const sequence = [targetNumber - 2, targetNumber - 1];

    return {
      id: `sequence-${Date.now()}-${Math.random()}`,
      targetNumber,
      sequence,
    };
  }, []);

  // Generate random target number (3-9)
  const generateRandomTargetNumber = useCallback(() => {
    const possibleTargets = [3, 4, 5, 6, 7, 8, 9];
    return possibleTargets[Math.floor(Math.random() * possibleTargets.length)];
  }, []);

  const generateNextSequence = useCallback(() => {
    let newTargetNumber;
    do {
      newTargetNumber = generateRandomTargetNumber();
    } while (currentSequence && newTargetNumber === currentSequence.targetNumber);
    return generateNumberSequence(newTargetNumber);
  }, [currentSequence, generateRandomTargetNumber, generateNumberSequence]);

  // Keep ref updated
  generateNextSequenceRef.current = generateNextSequence;

  const determineFactStatus = useCallback(
    (isCorrect: boolean, timeSpentMs: number): FactStatus => {
      if (!isCorrect) {
        return 'learning';
      }
      const timeSpentSeconds = timeSpentMs / 1000;
      if (timeSpentSeconds > FLUENCY_TARGETS_SEC[TRACK_ID]) {
        return 'accuracyPractice';
      } else {
        return 'mastered';
      }
    },
    []
  );

  const handleTestComplete = useCallback(
    async (results: DiagnosticAssessmentResult) => {
      if (user?.userId) {
        try {
          await recordPageTransition({
            userId: user.userId,
            trackId: TRACK_ID,
            page: 'other',
          });
        } catch (error) {
          console.error('Error recording final page transition:', error);
        }

        try {
          const result = await completePreKAssessment(user.userId, TRACK_ID, {
            totalQuestions: results.totalQuestions || 0,
            correctAnswers: results.correctAnswers || 0,
            activeTimeElapsed: results.activeTimeElapsed || 0,
          });
          if (result.focusTrack) {
            updateUserField({ focusTrack: result.focusTrack });
          }
        } catch (error) {
          console.error('Failed to save PreK assessment data:', error);
        }
      }
      onComplete(results);
    },
    [user?.userId, onComplete, updateUserField]
  );

  // Generate answer choices (target + both near-misses ±1 + one far miss ±2)
  const generateAnswerChoices = useCallback((target: number) => {
    const choices = [target];

    const nearMissPlus = target + 1;
    const nearMissMinus = target - 1;

    if (nearMissPlus >= 0 && nearMissPlus <= 10) {
      choices.push(nearMissPlus);
    }
    if (nearMissMinus >= 0 && nearMissMinus <= 10) {
      choices.push(nearMissMinus);
    }

    const farMissOptions = [target + 2, target - 2];
    const validFarMisses = farMissOptions.filter(
      (option) => option >= 0 && option <= 10 && !choices.includes(option)
    );

    if (validFarMisses.length > 0) {
      const randomFarMiss =
        validFarMisses[Math.floor(Math.random() * validFarMisses.length)];
      choices.push(randomFarMiss);
    }

    while (choices.length < 4) {
      const candidate = Math.floor(Math.random() * 11);
      if (!choices.includes(candidate)) {
        choices.push(candidate);
      }
    }

    return choices.sort(() => Math.random() - 0.5);
  }, []);

  const resetQuestionState = useCallback((callback?: () => void) => {
    isFirstQuestionRef.current = false;
    setTimerActive(false);
    setQuestionTimerActive(false);
    setQuestionTimeRemaining(TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);
    setWrongAnswer(null);
    setShowSequence(false);
    setTtsHasPlayed(false);
    setQuestionPhase('ready');
    setHasAnswered(false);
    setSelectedAnswer(null);

    if (callback) {
      setTimeout(callback, 50);
    }
  }, []);

  const startWhatComesNextSequence = useCallback(() => {
    setTtsHasPlayed(false);
    setTimerActive(false);

    if (isFirstQuestionRef.current) {
      setQuestionPhase('ready');
      setTimeout(() => {
        setQuestionPhase('question');
        setShowSequence(true);
      }, 500);
    } else {
      // Subsequent questions: skip "Get ready..." phase, show immediately
      setQuestionPhase('question');
      setShowSequence(true);
    }
  }, []);

  // ============================================================================
  // Effects
  // ============================================================================

  // Initialize first sequence
  useEffect(() => {
    if (!currentSequence) {
      const targetNumber = generateRandomTargetNumber();
      setCurrentSequence(generateNumberSequence(targetNumber));
    }
  }, [currentSequence, generateRandomTargetNumber, generateNumberSequence]);

  // Update answer choices when sequence changes
  useEffect(() => {
    if (currentSequence) {
      setAnswerChoices(generateAnswerChoices(currentSequence.targetNumber));
      setHasAnswered(false);
    }
  }, [currentSequence, generateAnswerChoices]);

  // Set track ID and report page transition on mount
  useEffect(() => {
    sessionStorage.setItem('activeTrackId', TRACK_ID);

    if (user?.userId) {
      recordPageTransition({
        userId: user.userId,
        trackId: TRACK_ID,
        page: 'assessment',
      }).catch((error: any) => {
        console.error('Error recording page transition:', error);
      });
    }
  }, [user?.userId]);

  // Start sequence when pattern changes
  useEffect(() => {
    if (currentSequence && questionPhase === 'ready') {
      startWhatComesNextSequence();
    }
  }, [currentSequence, questionPhase, startWhatComesNextSequence]);

  // Update question start time when moving to test phase
  useEffect(() => {
    if (!isWarmup) {
      questionStartTime.current = Date.now();
      accumulatedTimerTime.current = 0;
      timerActiveStartTime.current = 0;
      questionAccumulatedTimeRef.current = 0;
    }
  }, [isWarmup, currentSequence]);

  // Track timer active time
  useEffect(() => {
    if (isWarmup) return;

    if (timerActive) {
      timerActiveStartTime.current = Date.now();
      questionActiveStartRef.current = Date.now();
    } else {
      if (timerActiveStartTime.current > 0) {
        const activeTime = Date.now() - timerActiveStartTime.current;
        accumulatedTimerTime.current += activeTime;
        timerActiveStartTime.current = 0;
      }
      if (questionActiveStartRef.current) {
        const questionActiveTime = Date.now() - questionActiveStartRef.current;
        questionAccumulatedTimeRef.current += questionActiveTime;
        questionActiveStartRef.current = null;
      }
    }
  }, [timerActive, isWarmup]);

  // Question-count completion (replaces wall clock timer)
  useEffect(() => {
    if (!isWarmup && totalQuestions >= TOTAL_ASSESSMENT_QUESTIONS && user?.userId && !hasCompletedRef.current) {
      hasCompletedRef.current = true;
      handleTestComplete({
        userId: user.userId,
        testResults: [],
        recommendedStartSkill: 1,
        placementReason: `Completed ${TOTAL_ASSESSMENT_QUESTIONS} questions`,
        completedAt: new Date(),
        totalQuestions,
        correctAnswers,
        activeTimeElapsed,
      });
    }
  }, [totalQuestions, isWarmup, user?.userId, handleTestComplete, correctAnswers, activeTimeElapsed]);

  // Active time tracking
  useEffect(() => {
    if (isWarmup || !timerActive) return;

    const timer = setTimeout(() => {
      setActiveTimeElapsed((prev) => prev + TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [timerActive, isWarmup, activeTimeElapsed]);

  // Play TTS when entering question phase
  useEffect(() => {
    if (questionPhase === 'question' && !ttsHasPlayed) {
      const shouldPlayAudio = isWarmup
        ? warmupCount === 0
        : currentQuestionIndex === 0;

      if (settings.speechEnabled && shouldPlayAudio) {
        playTTSWithCallback(QUESTION_SCREEN_AUDIO.whatComesNext.instruction, () => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        }).catch(() => {
          if (!isWarmup) {
            setTimerActive(true);
            setQuestionTimerActive(true);
          }
          setTtsHasPlayed(true);
        });
      } else {
        if (!isWarmup) {
          setTimerActive(true);
          setQuestionTimerActive(true);
        }
        setTtsHasPlayed(true);
      }
    }
  }, [
    questionPhase,
    ttsHasPlayed,
    settings.speechEnabled,
    playTTSWithCallback,
    isWarmup,
    warmupCount,
    currentQuestionIndex,
  ]);

  // Per-question countdown timer
  useEffect(() => {
    if (isWarmup || !questionTimerActive || questionTimeRemaining <= 0 || hasAnswered) {
      if (
        questionTimeRemaining <= 0 &&
        questionTimerActive &&
        !isWarmup &&
        !hasAnswered
      ) {
        if (waitingForCorrectAnswer) {
          setWaitingForCorrectAnswer(false);
          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) {
            resetQuestionState();
            return;
          }
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentSequence(generateNumberSequence(questionSequenceRef.current[nextIndex])));
        } else {
          setTotalQuestions((prev) => prev + 1);

          if (user?.userId && currentSequence) {
            try {
              const factId = valueToFactId(TRACK_ID, currentSequence.targetNumber);
              const factStatus = determineFactStatus(false, TIMER_CONSTANTS.QUESTION_TIME_LIMITS[TRACK_ID]);

              progressQueue.enqueueProgressUpdate(user.userId, {
                facts: {
                  [factId]: {
                    attempts: 1,
                    correct: 0,
                    timeSpent:
                      questionAccumulatedTimeRef.current +
                      (questionActiveStartRef.current
                        ? Date.now() - questionActiveStartRef.current
                        : 0),
                    status: factStatus,
                  },
                },
              });
            } catch (error) {
              console.error('Error reporting Pre-K what comes next timeout progress:', error);
            }
          }

          const nextIndex = currentQuestionIndex + 1;
          if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) {
            resetQuestionState();
            return;
          }
          setCurrentQuestionIndex(nextIndex);
          resetQuestionState(() => setCurrentSequence(generateNumberSequence(questionSequenceRef.current[nextIndex])));
        }
      }
      return;
    }

    const timer = setTimeout(() => {
      setQuestionTimeRemaining((prev) => prev - TIMER_CONSTANTS.TICK_INTERVAL_MS);
    }, TIMER_CONSTANTS.TICK_INTERVAL_MS);

    return () => clearTimeout(timer);
  }, [
    questionTimeRemaining,
    questionTimerActive,
    isWarmup,
    waitingForCorrectAnswer,
    currentQuestionIndex,
    totalQuestions,
    hasAnswered,
    currentSequence,
    user?.userId,
    determineFactStatus,
    resetQuestionState,
    generateNumberSequence,
  ]);

  // ============================================================================
  // Warmup Progression
  // ============================================================================

  const advanceWarmupQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
    }

    const newWarmupCount = warmupCount + 1;

    // Check if warmup is complete (fixed 3 questions)
    if (newWarmupCount >= WARMUP_CONFIG.MAX_WARMUP_QUESTIONS) {
      // Show transition screen instead of immediately starting test
      setShowTransitionScreen(true);
      return;
    }

    setWarmupCount(newWarmupCount);
    resetQuestionState(() => setCurrentSequence(generateNextSequence()));
  }, [
    waitingForCorrectAnswer,
    warmupCount,
    generateNextSequence,
    resetQuestionState,
  ]);

  // Handle transition screen completion - start the actual test
  const handleTransitionComplete = useCallback(() => {
    setShowTransitionScreen(false);
    setIsWarmup(false);
    setWarmupCount(0);
    setCurrentQuestionIndex(0);
    const sequence = generateAssessmentSequence(TRACK_ID);
    questionSequenceRef.current = sequence;
    resetQuestionState(() => setCurrentSequence(generateNumberSequence(sequence[0])));
  }, [resetQuestionState, generateNumberSequence]);

  // ============================================================================
  // Test Progression
  // ============================================================================

  const advanceTestQuestion = useCallback(() => {
    if (waitingForCorrectAnswer) {
      setWaitingForCorrectAnswer(false);
      const nextIndex = currentQuestionIndex + 1;
      if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
      setCurrentQuestionIndex(nextIndex);
      resetQuestionState(() => setCurrentSequence(generateNumberSequence(questionSequenceRef.current[nextIndex])));
      return;
    }

    setCorrectAnswers((prev) => prev + 1);
    setTotalQuestions((prev) => prev + 1);
    const nextIndex = currentQuestionIndex + 1;
    if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
    setCurrentQuestionIndex(nextIndex);
    resetQuestionState(() => setCurrentSequence(generateNumberSequence(questionSequenceRef.current[nextIndex])));
  }, [
    waitingForCorrectAnswer,
    currentQuestionIndex,
    resetQuestionState,
    generateNumberSequence,
  ]);

  // ============================================================================
  // Answer Handler
  // ============================================================================

  const handleAnswerClick = useCallback(
    (selectedNumber: number) => {
      if (!currentSequence) return;
      if (waitingForCorrectAnswer && selectedNumber !== currentSequence.targetNumber)
        return;
      if (!waitingForCorrectAnswer && hasAnswered) return;

      if (!isWarmup) {
        setTimerActive(true);
        setQuestionTimerActive(true);
      }
      setTtsHasPlayed(true);
      setHasAnswered(true);
      setSelectedAnswer(selectedNumber);

      const isCorrect = selectedNumber === currentSequence.targetNumber;

      let timerActiveResponseTime = 0;
      if (!isWarmup) {
        timerActiveResponseTime =
          questionAccumulatedTimeRef.current +
          (questionActiveStartRef.current
            ? Date.now() - questionActiveStartRef.current
            : 0);
      }

      if (!isWarmup && user?.userId && !waitingForCorrectAnswer) {
        try {
          const factId = valueToFactId(TRACK_ID, currentSequence.targetNumber);
          const factStatus = determineFactStatus(isCorrect, timerActiveResponseTime);

          progressQueue.enqueueProgressUpdate(user.userId, {
            facts: {
              [factId]: {
                attempts: 1,
                correct: isCorrect ? 1 : 0,
                timeSpent: timerActiveResponseTime,
                status: factStatus,
              },
            },
          });
        } catch (error) {
          console.error('Error reporting Pre-K what comes next progress:', error);
        }
      }

      if (isWarmup) {
        if (isCorrect) {
          playChime().then(() => {
            setTimeout(() => {
              advanceWarmupQuestion();
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          });
        } else {
          setWrongAnswer(selectedNumber);
          setTimeout(() => setWrongAnswer(null), TIMER_CONSTANTS.WRONG_ANSWER_FLASH_MS);

          if (settings.speechEnabled) {
            setTimeout(() => {
              playTTSWithCallback(QUESTION_SCREEN_AUDIO.whatComesNext.instruction).catch(
                () => {}
              );
            }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
          }

          setWaitingForCorrectAnswer(true);
          setHasAnswered(false);
        }
      } else {
        // Quiz mode: neutral feedback with chime for all answers, same timing
        if (!waitingForCorrectAnswer && !isCorrect) {
          setTotalQuestions((prev) => prev + 1);
        }

        playChime().then(() => {
          setTimeout(() => {
            if (isCorrect) {
              advanceTestQuestion();
            } else {
              const nextIndex = currentQuestionIndex + 1;
              if (nextIndex >= TOTAL_ASSESSMENT_QUESTIONS) return;
              setCurrentQuestionIndex(nextIndex);
              resetQuestionState(() =>
                setCurrentSequence(generateNumberSequence(questionSequenceRef.current[nextIndex]))
              );
            }
          }, TIMER_CONSTANTS.CORRECT_ANSWER_DELAY_MS);
        });
      }
    },
    [
      currentSequence,
      isTTSPlaying,
      isWarmup,
      user,
      waitingForCorrectAnswer,
      hasAnswered,
      settings.speechEnabled,
      determineFactStatus,
      playChime,
      playTTSWithCallback,
      advanceWarmupQuestion,
      advanceTestQuestion,
      currentQuestionIndex,
      resetQuestionState,
      generateNumberSequence,
    ]
  );

  // ============================================================================
  // Render
  // ============================================================================

  if (!currentSequence) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  // Show transition screen after warmup completes
  if (showTransitionScreen) {
    return (
      <WarmupTransitionScreen
        onComplete={handleTransitionComplete}
        speechEnabled={settings.speechEnabled}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col justify-center">
      {/* Header */}
      <AssessmentHeader
        isWarmup={isWarmup}
        showTimer={false}
        wallClockTimeRemaining={0}
        wallClockStarted={false}
        isTTSPlaying={isTTSPlaying}
      />

      {/* Listen Button - only during question phase */}
      {settings.speechEnabled && questionPhase === 'question' && (
        <div className="flex justify-center pt-4">
          <ListenButton
            onClick={() => playTTSWithCallback(QUESTION_SCREEN_AUDIO.whatComesNext.instruction)}
            disabled={hasAnswered}
            isPlaying={isTTSPlaying}
          />
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-6xl flex items-center justify-center gap-24">
          {/* Ready Phase - only shown on first question */}
          {questionPhase === 'ready' && isFirstQuestionRef.current && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
                Get ready...
              </div>
              <div className="text-lg text-gray-500">What comes next?</div>
            </div>
          )}

          {/* Question Phase */}
          {questionPhase === 'question' && (
            <>
              {/* Left Side: Number Sequence Display */}
              {showSequence && (
                <div
                  className="flex-shrink-0"
                  style={{ width: '300px', height: '400px' }}
                >
                  <div className="relative w-full h-full flex flex-col items-center justify-center">
                    {/* Number sequence display */}
                    <div className="flex items-center gap-6 mb-8">
                      {currentSequence.sequence.map((number, index) => (
                        <motion.div
                          key={`seq-${index}`}
                          initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
                          animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
                          transition={isFirstQuestionRef.current ? { duration: 0.4, delay: index * 0.2 } : { duration: 0.15 }}
                          className="w-20 h-20 lg:w-24 lg:h-24 bg-blue-600 text-white rounded-3xl flex items-center justify-center text-4xl lg:text-5xl font-bold shadow-lg"
                          style={NUMERAL_FONT_STYLE}
                        >
                          {number}
                        </motion.div>
                      ))}

                      {/* Question mark */}
                      <motion.div
                        initial={isFirstQuestionRef.current ? { scale: 0, opacity: 0 } : { opacity: 0 }}
                        animate={isFirstQuestionRef.current ? { scale: 1, opacity: 1 } : { opacity: 1 }}
                        transition={isFirstQuestionRef.current ? { duration: 0.4, delay: currentSequence.sequence.length * 0.2 } : { duration: 0.15 }}
                        className="w-20 h-20 lg:w-24 lg:h-24 bg-gray-300 text-gray-600 rounded-3xl flex items-center justify-center text-4xl lg:text-5xl font-bold shadow-lg border-4 border-dashed border-gray-500"
                        style={NUMERAL_FONT_STYLE}
                      >
                        ?
                      </motion.div>
                    </div>

                    {/* Instruction text - only shown on first question */}
                    {isFirstQuestionRef.current && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.6 }}
                        className="text-xl lg:text-2xl font-semibold text-gray-700 text-center"
                      >
                        What comes next?
                      </motion.div>
                    )}
                  </div>
                </div>
              )}

              {/* Right Side: Answer Choices */}
              <div className="w-full max-w-md lg:max-w-lg">
                <AnswerChoicesGrid
                  choices={answerChoices}
                  correctAnswer={currentSequence.targetNumber}
                  wrongAnswer={wrongAnswer}
                  hasAnswered={hasAnswered}
                  waitingForCorrectAnswer={waitingForCorrectAnswer}
                  disabled={false}
                  onChoiceClick={handleAnswerClick}
                  selectedAnswer={selectedAnswer}
                  neutralFeedbackMode={!isWarmup}
                  minimalAnimation={!isFirstQuestionRef.current}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatComesNextQuestionScreen;
